import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useUpdateClientDashboard } from '@/queries/clientDashboards'
import { useSupportAdmins } from '@/queries/settings'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { memberName } from '@/types/clientDashboard'
import type { ClientDashboard } from '@/types/clientDashboard'
import { LoadError } from './LoadError'

interface Choice {
  _id: string
  name: string
  email?: string
  leftSupportTeam: boolean
}

const sameIds = (left: string[], right: string[]) => left.length === right.length && left.every(id => right.includes(id))

export function AccountTeamEditor({ dashboard }: { dashboard: ClientDashboard }) {
  const supportTeam = useSupportAdmins()
  const update = useUpdateClientDashboard(dashboard.companyId)
  const savedIds = dashboard.accountTeam.map(member => member._id)
  // Hold a draft only while edited, so refetched data shows through an untouched form.
  const [draft, setDraft] = useState<string[] | null>(null)
  const selected = draft ?? savedIds

  const supportChoices: Choice[] = (supportTeam.data?.data ?? []).map(admin => ({
    _id: admin._id,
    name: admin.name || admin.email || admin._id,
    email: admin.email,
    leftSupportTeam: false,
  }))
  // Keep listing members who have left the support team so nobody drops off unnoticed.
  const formerChoices: Choice[] = dashboard.accountTeam
    .filter(member => !supportChoices.some(choice => choice._id === member._id))
    .map(member => ({ _id: member._id, name: memberName(member), email: member.email, leftSupportTeam: !member.onSupportTeam }))
  const choices = [...supportChoices, ...formerChoices]

  function toggle(userId: string, checked: boolean) {
    setDraft(checked ? [...selected, userId] : selected.filter(id => id !== userId))
  }

  async function save() {
    const listed = choices.filter(choice => selected.includes(choice._id)).map(choice => choice._id)
    const accountTeamUserIds = [...listed, ...selected.filter(id => !listed.includes(id))]
    try {
      await update.mutateAsync({ accountTeamUserIds })
      setDraft(null)
      toast.success('Account team saved')
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not save the account team'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account team</CardTitle>
        <CardDescription>The Etainabl people who look after this client, chosen from the support team.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {supportTeam.isError && !supportTeam.data ? (
          <LoadError title="Support team didn't load" onRetry={() => supportTeam.refetch()} />
        ) : (
          <ul className="max-h-96 divide-y divide-border overflow-y-auto rounded-md border">
            {choices.map(choice => (
              <li key={choice._id} className="hover:bg-muted">
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    aria-label={choice.name}
                    checked={selected.includes(choice._id)}
                    onChange={e => toggle(choice._id, e.target.checked)}
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-foreground">{choice.name}</span>
                    {choice.email && <span className="block truncate text-xs text-muted-foreground">{choice.email}</span>}
                  </span>
                  {choice.leftSupportTeam && <span className="ml-auto text-xs text-etn-warning-text">No longer on the support team</span>}
                </label>
              </li>
            ))}
          </ul>
        )}
        <Button onClick={save} disabled={sameIds(selected, savedIds) || update.isPending}>
          Save account team
        </Button>
      </CardContent>
    </Card>
  )
}
